/**
 * DeviceApprovals.js
 *
 * Admin Device Approvals Dashboard
 * Allows admins to review, approve, and reject pending device approvals
 *
 * Features:
 * - View all pending device approvals in professional table format
 * - Filter by status (Pending, Approved, Rejected, All)
 * - View device details before approving
 * - Approve with optional comments
 * - Reject with required reason
 * - Track approval history with statistics
 * - View resubmission counts
 * - Uses Tables component for professional UX
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig';
import './DeviceApprovals.css';
import '../Transfers/DeviceTransfers.css';
import EmptyState from '../../features/dashboard/widgets/Shared/EmptyState';
import ErrorState from '../../features/dashboard/widgets/Shared/ErrorState';
import AdminApprovalsTable from '../../features/dashboard/widgets/Tables/AdminApprovalsTable';
import ApprovalModal from '../../features/approvals/components/ApprovalModal';
import Pagination from '../../shared/components/common/Pagination';
import ListSearchBar from '../../shared/components/common/ListSearchBar';
import TransferActionBanner from '../../features/transfers/components/TransferActionBanner';
import { getListPageSize, saveUiPreferencePatch } from '../../shared/utils/uiPreferences';
import { useServerTableSort } from '../../shared/utils/serverTableSort';
const DeviceApprovals = ({ token, user, onActionCountsChange }) => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [departments, setDepartments] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const searchDebounceRef = useRef(null);
  const [stats, setStats] = useState({
    pending_count: 0,
    approved_count: 0,
    rejected_count: 0,
    total_count: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: getListPageSize(),
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  const { sorting, handleSortChange } = useServerTableSort('requested_at', 'desc');

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [modalNotification, setModalNotification] = useState({
    show: false,
    type: 'success', // 'success' | 'error'
    message: ''
  });

  const fetchApprovals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        status: activeTab,
        page: pagination.page,
        per_page: pagination.per_page,
        sort_by: sorting.sort_by,
        sort_direction: sorting.sort_direction,
      };
      if (search) {
        params.search = search;
      }

      const response = await axios.get(
        API_ENDPOINTS.APPROVALS.PENDING,
        {
          params,
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data && response.data.approvals) {
        setApprovals(response.data.approvals);
        setStats({
          pending_count: response.data.pending_count || 0,
          approved_count: response.data.approved_count || 0,
          rejected_count: response.data.rejected_count || 0,
          total_count: response.data.total_count || 0
        });
        if (response.data.pagination) {
          setPagination({
            page: response.data.pagination.page,
            per_page: response.data.pagination.per_page,
            total: response.data.pagination.total || 0,
            total_pages: response.data.pagination.total_pages || 0,
            has_next: response.data.pagination.has_next || false,
            has_prev: response.data.pagination.has_prev || false,
          });
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch approvals');
      console.error('Error fetching approvals:', err);
    } finally {
      setLoading(false);
    }
  }, [token, activeTab, pagination.page, pagination.per_page, search, sorting.sort_by, sorting.sort_direction]);

  // Fetch departments for department icons
  const fetchDepartments = async () => {
    try {
      const response = await axios.get(
        API_ENDPOINTS.DEPARTMENTS.LIST,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.data?.departments) {
        setDepartments(response.data.departments);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  useEffect(() => {
    if (token) {
      fetchApprovals();
    }
  }, [token, fetchApprovals]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Fetch departments for department icons
  useEffect(() => {
    if (token) {
      fetchDepartments();
    }
  }, [token]);

  const handleSortChangeWithReset = (column) => {
    handleSortChange(column);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    if (!value) {
      setSearch('');
      setPagination((prev) => ({ ...prev, page: 1 }));
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      setSearch(value);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 300);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
  };

  // Handle approve action
  const handleApprove = async (approvalId, reason = '') => {
    try {
      await axios.post(
        API_ENDPOINTS.APPROVALS.APPROVE(approvalId),
        { decision_reason: reason },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setModalNotification({
        show: true,
        type: 'success',
        message: 'Device approved successfully!'
      });

      setTimeout(() => {
        setModalOpen(false);
        setModalType(null);
        setModalData(null);
        setModalNotification({ show: false, type: 'success', message: '' });
        fetchApprovals();
        if (typeof onActionCountsChange === 'function') {
          onActionCountsChange();
        }
      }, 3000);
    } catch (err) {
      setModalNotification({
        show: true,
        type: 'error',
        message: err.response?.data?.error || 'Failed to approve device'
      });
      console.error('Error approving device:', err);
    }
  };

  // Handle reject action
  const handleReject = async (approvalId, reason) => {
    if (!reason.trim()) {
      setModalNotification({
        show: true,
        type: 'error',
        message: 'Please provide a rejection reason'
      });
      return;
    }

    try {
      await axios.post(
        API_ENDPOINTS.APPROVALS.REJECT(approvalId),
        { rejection_reason: reason },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setModalNotification({
        show: true,
        type: 'success',
        message: 'Device rejected and manager notified'
      });

      setTimeout(() => {
        setModalOpen(false);
        setModalType(null);
        setModalData(null);
        setModalNotification({ show: false, type: 'success', message: '' });
        fetchApprovals();
        if (typeof onActionCountsChange === 'function') {
          onActionCountsChange();
        }
      }, 3000);
    } catch (err) {
      setModalNotification({
        show: true,
        type: 'error',
        message: err.response?.data?.error || 'Failed to reject device'
      });
      console.error('Error rejecting device:', err);
    }
  };

  // Render loading state
  if (loading && approvals.length === 0) {
    return (
      <div className="device-approval-dashboard">
        <div className="approval-dashboard-loading">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading approvals...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && !approvals.length) {
    return (
      <ErrorState
        error={error}
        onRetry={() => fetchApprovals()}
      />
    );
  }

  return (
    <div className="device-approval-dashboard">
      <div className="approval-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1>✅ Device Approvals</h1>
          <button onClick={() => fetchApprovals()} className="btn-refresh" title="Refresh approvals" style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer', lineHeight: 1 }}>
            🔄
          </button>
        </div>
        <p>Review and approve/reject pending device submissions from managers</p>
      </div>

      <TransferActionBanner
        actions={
          stats.pending_count > 0
            ? [{ key: 'pending', label: 'Pending', count: stats.pending_count }]
            : []
        }
        onSelectAction={(tab) => handleTabChange(tab)}
      />

      {/* Status filters */}
      <div className="approval-tabs" role="tablist" aria-label="Approval status">
        <button
          type="button"
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => handleTabChange('pending')}
          title="Pending Approvals"
        >
          <span className="tab-label">Pending</span>
          <span className="tab-count">{stats.pending_count}</span>
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'approved' ? 'active' : ''}`}
          onClick={() => handleTabChange('approved')}
          title="Approved Devices"
        >
          <span className="tab-label">Approved</span>
          <span className="tab-count">{stats.approved_count}</span>
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'rejected' ? 'active' : ''}`}
          onClick={() => handleTabChange('rejected')}
          title="Rejected Devices"
        >
          <span className="tab-label">Rejected</span>
          <span className="tab-count">{stats.rejected_count}</span>
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => handleTabChange('all')}
          title="All Devices"
        >
          <span className="tab-label">All</span>
          <span className="tab-count">{stats.total_count}</span>
        </button>
      </div>

      <ListSearchBar
        value={searchInput}
        onChange={handleSearchChange}
        onClear={handleClearSearch}
        placeholder="Search device ID, name, department, or manager…"
      />

      {/* Approvals table (workflow list UX — same as transfer pages) */}
      <div className="approval-table-container">
        {approvals.length === 0 ? (
          <EmptyState
            section="Approvals"
            message={
              search
                ? 'No approvals match your search in this tab.'
                : activeTab === 'pending'
                  ? 'No pending approvals! All devices are reviewed.'
                  : `No ${activeTab} approvals to display.`
            }
            icon={activeTab === 'pending' ? '✅' : '📋'}
          />
        ) : (
          <AdminApprovalsTable
            data={approvals}
            onViewDevice={(approval) => {
              setModalOpen(true);
              setModalType('view');
              setModalData(approval);
            }}
            loading={loading}
            departments={departments}
            sorting={sorting}
            onSortChange={handleSortChangeWithReset}
          />
        )}
      </div>

      {pagination.total > 0 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          onPageChange={(newPage) => setPagination((prev) => ({ ...prev, page: newPage }))}
          perPage={pagination.per_page}
          onPerPageChange={(newPerPage) => {
            saveUiPreferencePatch({ list_page_size: newPerPage });
            setPagination((prev) => ({ ...prev, page: 1, per_page: newPerPage }));
          }}
          totalItems={pagination.total}
          itemLabel="approvals"
        />
      )}

      {/* Approval Modal - Uses React Portal */}
      <ApprovalModal
        modal={
          modalOpen
            ? {
                type: modalType,
                data: modalData
              }
            : null
        }
        onClose={() => {
          setModalOpen(false);
          setModalType(null);
          setModalData(null);
          setModalNotification({ show: false, type: 'success', message: '' });
        }}
        onApprove={handleApprove}
        onReject={handleReject}
        notification={modalNotification}
        onDismissNotification={() => {
          setModalNotification({ show: false, type: 'success', message: '' });
        }}
        token={token}
        user={user}
      />
    </div>
  );
};

export default DeviceApprovals;
